import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import LeadCapture from "../components/LeadCapture";
import { trackCTA } from "../lib/analytics";
import { TID } from "../lib/testIds";
import {
  ArrowRight,
  Target,
  ShieldCheck,
  Gauge,
  FileSearch,
  Network,
  ClipboardCheck,
  Award,
  AlertTriangle,
  Building2,
  Activity,
  UserCheck,
  Sparkles,
} from "lucide-react";

const valueProps = [
  { icon: Target, title: "Bid / No-Bid Intelligence", body: "Decisive go/no-go signal grounded in FAR, DFARS, and capture economics — not summarization." },
  { icon: Gauge, title: "Opportunity Score (0-100)", body: "Composite scorecard across 10 weighted dimensions for executive review." },
  { icon: ShieldCheck, title: "Compliance Forensics", body: "Disqualification risks, set-asides, cyber, wage, and clause exposure." },
  { icon: AlertTriangle, title: "Hidden Red Flags", body: "Surfaces what experienced capture managers see — and what novices miss." },
  { icon: FileSearch, title: "FAR / DFARS Analysis", body: "Clause-by-clause impact and risk ranking on the contractor." },
  { icon: Network, title: "Contractor Match", body: "NAICS, set-aside, vehicle, clearance, geographic, and past performance fit." },
];

const sections = [
  "Executive Summary",
  "Requirement Analysis",
  "Procurement Profile",
  "NAICS & Small Business",
  "Procurement Timeline",
  "Evaluation Criteria",
  "Compliance Review",
  "FAR / DFARS Analysis",
  "Resource Requirements",
  "Competitive Landscape",
  "Opportunity Risks",
  "Hidden Red Flags",
  "Proposal Effort Estimate",
  "Contractor Match",
  "Contractor Readiness",
  "Win Probability",
  "Top 10 Pre-Bid Actions",
  "GovBidIQ Scorecard",
  "Executive Pursuit Rec.",
  "Final Recommendation",
];

const verdictTones = {
  "Strong Bid": "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  "Bid with Caution": "bg-amber-500/15 text-amber-300 border-amber-500/40",
  "No Bid": "bg-rose-500/15 text-rose-300 border-rose-500/40",
};

const SAMPLE_REPORTS = [
  {
    agency: "USACE",
    solno: "W912DY-26-R-0042",
    naics: "541512 · Small Bus.",
    title: "Cybersecurity Engineering & RMF Support IDIQ",
    verdict: "Strong Bid",
    verdictTone: verdictTones["Strong Bid"],
    scores: { opp: 85, match: 89, roi: 78, win: "High" },
    summary:
      "Total small business set-aside with CMMC L2 floor by Oct 2026. SDVOSB contractor with TS facility clearance, direct DISA RMF past performance, and Huntsville footprint creates structural advantage. Compliance cost is moderate but absorbed.",
  },
  {
    agency: "VA",
    solno: "36C10X-26-Q-0218",
    naics: "621399 · WOSB",
    title: "Telehealth Clinical Services — VISN Multiple Award",
    verdict: "Bid with Caution",
    verdictTone: verdictTones["Bid with Caution"],
    scores: { opp: 71, match: 58, roi: 54, win: "Moderate" },
    summary:
      "VA Schedule 621I leverages strong telehealth past performance, but credentialing burden across 6 VISNs and aggressive PWS metrics raise scope-risk. Pursuit ROI is marginal without an established VA clinical partner.",
  },
  {
    agency: "NASA",
    solno: "80GSFC26R0017",
    naics: "541330 · Large Bus.",
    title: "Mission Operations Engineering — Goddard Spaceflight Center",
    verdict: "Bid with Caution",
    verdictTone: verdictTones["Bid with Caution"],
    scores: { opp: 88, match: 64, roi: 61, win: "Moderate" },
    summary:
      "High-revenue 7-year ceiling and aligned core capabilities, but incumbent has 11 years of mission heritage. Win path requires senior key personnel commitments and discriminating mission-assurance approach.",
  },
  {
    agency: "GSA",
    solno: "47QFCA-26-R-0009",
    naics: "541611 · 8(a)",
    title: "OASIS+ 8(a) Pool 1 — Management Consulting On-Ramp",
    verdict: "Strong Bid",
    verdictTone: verdictTones["Strong Bid"],
    scores: { opp: 92, match: 81, roi: 87, win: "High" },
    summary:
      "Strategic vehicle-on-ramp. Self-scoring favors contractors with diverse 8(a) past performance and CMMI L3. Modest proposal cost relative to 10-year revenue pipeline = exceptional Pursuit ROI.",
  },
];

const PRICE_TEASERS = [
  { id: "opportunity", name: "Opportunity Intelligence", tagline: "Per-RFP triage", price: 49 },
  { id: "executive", name: "Executive Bid/No-Bid", tagline: "C-suite briefing", price: 99 },
  { id: "capture", name: "Capture Intelligence", tagline: "Strategic pursuit", price: 249 },
];

export default function Landing() {
  return (
    <div className="grain">
      <Navbar />

      {/* HERO */}
      <section data-testid={TID.landingHero} className="relative border-b border-[var(--border-soft)] overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(180deg, black 0%, transparent 90%)",
            WebkitMaskImage: "linear-gradient(180deg, black 0%, transparent 90%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[var(--bg-primary)]/30 to-[var(--bg-primary)] pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 border border-sky-500/40 bg-sky-500/5 px-3 py-1.5 mb-8">
              <span className="w-1.5 h-1.5 bg-sky-400 pulse-dot" />
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-sky-300">
                Government Contract Intelligence · v1.0
              </span>
            </div>

            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-slate-100 leading-[1.02] mb-6">
              Bid the right opportunities.
              <br />
              <span className="text-slate-500">Walk away from the wrong ones.</span>
            </h1>

            <p className="text-slate-400 text-base sm:text-lg max-w-2xl leading-relaxed mb-10">
              GovBidIQ is an executive-grade decision intelligence platform for federal contractors.
              Upload a solicitation, define your capabilities, and receive a 20-section Opportunity
              Intelligence Report with a defensible Bid / No-Bid recommendation in minutes.
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-6">
              <Link
                to="/discovery"
                data-testid="landing-cta-ai-discovery"
                onClick={() => trackCTA("landing-hero-ai-discovery")}
                className="inline-flex items-center gap-3 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-xs tracking-[0.2em] uppercase font-bold px-6 py-3.5 transition-colors"
              >
                <Sparkles className="w-4 h-4" strokeWidth={2.5} />
                Start AI Discovery
              </Link>
              <a
                href="#free-assessment"
                data-testid="landing-cta-free-assessment"
                onClick={() => trackCTA("landing-hero-free-assessment")}
                className="inline-flex items-center gap-3 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-xs tracking-[0.2em] uppercase font-bold px-6 py-3.5 transition-colors"
              >
                Get Free Assessment
              </a>
              <Link
                to="/pricing"
                data-testid={TID.landingCta}
                onClick={() => trackCTA("landing-hero-pricing")}
                className="inline-flex items-center gap-3 text-slate-400 hover:text-sky-400 font-mono text-xs tracking-[0.2em] uppercase font-bold px-2 py-3.5 transition-colors"
              >
                View Pricing <ArrowRight className="w-4 h-4" strokeWidth={3} />
              </Link>
            </div>

            {/* Phase 1 + 2 — secondary CTA strip */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-12 text-xs font-mono">
              <Link to="/tour" data-testid="landing-cta-tour" onClick={() => trackCTA("landing-hero-tour")} className="text-slate-400 hover:text-sky-400 tracking-[0.18em] uppercase inline-flex items-center gap-1.5">
                <span className="w-1 h-1 bg-sky-500" /> Product Tour
              </Link>
              <Link to="/demo-library" data-testid="landing-cta-demo-library" onClick={() => trackCTA("landing-hero-demo-library")} className="text-slate-400 hover:text-sky-400 tracking-[0.18em] uppercase inline-flex items-center gap-1.5">
                <span className="w-1 h-1 bg-sky-500" /> Demo Library
              </Link>
              <Link to="/sample" data-testid="landing-cta-sample" onClick={() => trackCTA("landing-hero-sample")} className="text-slate-400 hover:text-sky-400 tracking-[0.18em] uppercase inline-flex items-center gap-1.5">
                <span className="w-1 h-1 bg-sky-500" /> Sample Report
              </Link>
              <Link to="/strategy-call" data-testid="landing-cta-strategy" onClick={() => trackCTA("landing-hero-strategy")} className="text-slate-400 hover:text-sky-400 tracking-[0.18em] uppercase inline-flex items-center gap-1.5">
                <span className="w-1 h-1 bg-sky-500" /> Strategy Call
              </Link>
              <Link to="/use-cases" data-testid="landing-cta-use-cases" onClick={() => trackCTA("landing-hero-use-cases")} className="text-slate-400 hover:text-sky-400 tracking-[0.18em] uppercase inline-flex items-center gap-1.5">
                <span className="w-1 h-1 bg-sky-500" /> Use Cases
              </Link>
              <Link to="/learning" data-testid="landing-cta-learning" onClick={() => trackCTA("landing-hero-learning")} className="text-slate-400 hover:text-sky-400 tracking-[0.18em] uppercase inline-flex items-center gap-1.5">
                <span className="w-1 h-1 bg-sky-500" /> Academy
              </Link>
            </div>

            <div className="grid grid-cols-3 gap-px bg-[var(--border-soft)] max-w-xl">
              {[
                { k: "20", v: "Analysis Sections" },
                { k: "0-100", v: "GovBidIQ Score" },
                { k: "< 5m", v: "Avg Turnaround" },
              ].map((s) => (
                <div key={s.v} className="bg-[var(--bg-primary)] p-4">
                  <div className="metric-number text-2xl text-sky-400">{s.k}</div>
                  <div className="tick-label mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right rail: terminal-style preview */}
          <div className="lg:col-span-4">
            <div className="panel-elev p-5">
              <div className="flex items-center justify-between border-b border-[var(--border-soft)] pb-3 mb-4">
                <div className="tick-label">GovBidIQ // Live Sample</div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-emerald-400" />
                  <span className="font-mono text-[9px] tracking-widest text-emerald-400 uppercase">Ready</span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="tick-label mb-1">Opportunity Score</div>
                  <div className="flex items-end gap-2">
                    <span className="metric-number text-5xl text-sky-400">82</span>
                    <span className="font-mono text-xs text-slate-500 pb-2">/100</span>
                  </div>
                </div>

                <div className="divider-h" />

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="tick-label mb-1">Match</div>
                    <div className="metric-number text-2xl text-slate-100">71<span className="text-slate-500 text-base">/100</span></div>
                  </div>
                  <div>
                    <div className="tick-label mb-1">Win Prob.</div>
                    <div className="inline-block bg-amber-500/15 text-amber-300 font-mono text-[10px] tracking-widest uppercase px-2 py-1 border border-amber-500/30">
                      Moderate
                    </div>
                  </div>
                </div>

                <div className="divider-h" />

                <div>
                  <div className="tick-label mb-1.5">Final Recommendation</div>
                  <div className="inline-block bg-emerald-500/15 text-emerald-300 font-mono text-[10px] tracking-widest uppercase px-2.5 py-1 border border-emerald-500/40">
                    Strong Bid
                  </div>
                </div>

                <div className="divider-h" />

                <div className="space-y-1.5">
                  {["Solicitation parsed", "FAR / DFARS analyzed", "Match scored", "Risks surfaced"].map((s) => (
                    <div key={s} className="flex items-center justify-between text-[11px] font-mono">
                      <span className="text-slate-400">{s}</span>
                      <span className="text-emerald-400">[OK]</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section id="how" className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="tick-label mb-3 text-sky-400">/ Capabilities</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-slate-100 max-w-2xl leading-tight">
                Decision intelligence — not document summarization.
              </h2>
            </div>
            <p className="text-slate-400 max-w-md text-sm leading-relaxed">
              Built on Claude Sonnet 4.5 and informed by FAR, DFARS, source-selection methodology, and
              capture-management discipline.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-soft)]">
            {valueProps.map((v) => (
              <div key={v.title} className="bg-[var(--bg-primary)] p-7 hover:bg-[var(--bg-secondary)] transition-colors group">
                <v.icon className="w-6 h-6 text-sky-400 mb-5" strokeWidth={1.6} />
                <h3 className="font-display font-bold text-slate-100 text-base mb-2">{v.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PIPELINE */}
      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="tick-label mb-3 text-sky-400">/ Workflow</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-slate-100 mb-12">
            From RFP to recommendation in four steps.
          </h2>

          <div className="grid lg:grid-cols-4 gap-px bg-[var(--border-soft)]">
            {[
              { n: "01", t: "Ingest", d: "Upload PDF / DOCX or paste the SOW/PWS, attachments, and amendments." , icon: FileSearch },
              { n: "02", t: "Profile", d: "Submit your contractor capability sheet — NAICS, set-asides, past performance.", icon: Building2 },
              { n: "03", t: "Analyze", d: "Claude Sonnet 4.5 runs 20 evaluations against FAR/DFARS and source-selection logic.", icon: Activity },
              { n: "04", t: "Decide", d: "Receive scored, defensible Bid / No-Bid recommendation with action items.", icon: ClipboardCheck },
            ].map((s) => (
              <div key={s.n} className="bg-[var(--bg-primary)] p-7 relative">
                <div className="flex items-start justify-between mb-6">
                  <span className="metric-number text-3xl text-slate-700">{s.n}</span>
                  <s.icon className="w-5 h-5 text-sky-400" strokeWidth={1.6} />
                </div>
                <h3 className="font-display font-bold text-slate-100 mb-2">{s.t}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTIONS LIST */}
      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="tick-label mb-3 text-sky-400">/ Inside the Report</div>
              <h2 className="font-display font-black text-3xl text-slate-100 mb-4 leading-tight">
                Twenty sections. One defensible decision.
              </h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Each report is structured the way a senior capture executive would brief their CEO:
                signal first, evidence next, action last.
              </p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 font-mono text-xs tracking-[0.2em] uppercase border-b border-sky-500/40"
              >
                Generate Report <ArrowRight className="w-3 h-3" strokeWidth={3} />
              </Link>
            </div>
            <div className="lg:col-span-8 grid grid-cols-2 gap-px bg-[var(--border-soft)]">
              {sections.map((s, i) => (
                <div key={s} className="bg-[var(--bg-primary)] px-5 py-3.5 flex items-center gap-4">
                  <span className="font-mono text-[10px] text-slate-600 w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-slate-300 text-sm">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SAMPLE REPORTS */}
      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="tick-label text-sky-400 mb-3">/ Sample Reports</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-slate-100 leading-tight max-w-2xl">
                Real opportunity types. Real intelligence outputs.
              </h2>
            </div>
            <p className="text-slate-400 max-w-md text-sm leading-relaxed">
              Representative GovBidIQ outputs across federal civilian, defense, intelligence, and
              health agency opportunities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border-soft)]">
            {SAMPLE_REPORTS.map((s) => (
              <div key={s.agency} data-testid={`sample-${s.agency.toLowerCase()}`} className="bg-[var(--bg-primary)] p-7 hover:bg-[var(--bg-secondary)] transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-sky-400 mb-1">{s.agency}</div>
                    <h3 className="font-display font-bold text-slate-100 text-lg leading-tight">{s.title}</h3>
                    <div className="font-mono text-xs text-slate-500 mt-1">{s.solno} · {s.naics}</div>
                  </div>
                  <div className={`font-mono text-[10px] tracking-widest uppercase px-2.5 py-1 border ${s.verdictTone}`}>
                    {s.verdict}
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-px bg-[var(--border-soft)] mb-4">
                  {[
                    { l: "Opp", v: s.scores.opp },
                    { l: "Match", v: s.scores.match },
                    { l: "ROI", v: s.scores.roi },
                    { l: "Win", v: s.scores.win, isText: true },
                  ].map((m) => (
                    <div key={m.l} className="bg-[var(--bg-secondary)] p-3">
                      <div className="tick-label mb-1">{m.l}</div>
                      <div className={`metric-number ${m.isText ? "text-sm" : "text-xl"} text-slate-100`}>
                        {m.v}{!m.isText && <span className="text-slate-500 text-xs">/100</span>}
                      </div>
                    </div>
                  ))}
                </div>

                <p className="text-sm text-slate-400 leading-relaxed">{s.summary}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-slate-500 mt-6 font-mono">
            * Sample outputs are representative of GovBidIQ analysis. Real reports are tailored to
            your contractor profile and the exact solicitation provided.
          </p>
        </div>
      </section>

      {/* FOUNDER CREDIBILITY */}
      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1200px] mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="tick-label text-sky-400 mb-3">/ Built By Practitioners</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-slate-100 leading-tight mb-5">
                Nearly two decades on
                <br />
                <span className="text-slate-500">both sides of the table.</span>
              </h2>
              <p className="text-slate-300 text-base leading-relaxed mb-6">
                GovBidIQ is built on nearly 20 years of federal and commercial contracting
                experience — capture management, source-selection panels, proposal evaluation,
                contract administration, and small business programs. The same methodology that
                $100M+ primes apply internally, encoded into an AI-assisted report you can run on
                any solicitation in your pipeline.
              </p>
              <div className="space-y-3 mb-7">
                {[
                  "Federal source selection & evaluation panel experience",
                  "Commercial contracting and capture management exposure",
                  "FAR / DFARS clause-level fluency and source-selection methodology",
                  "AI-assisted analysis for faster, defensible Bid/No-Bid decisions",
                ].map((line) => (
                  <div key={line} className="flex items-start gap-3 text-sm text-slate-300">
                    <UserCheck className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" />
                    <span>{line}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/about"
                data-testid="founder-cred-about-cta"
                onClick={() => trackCTA("founder-cred-learn-more")}
                className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 font-mono text-xs tracking-[0.2em] uppercase border-b border-sky-500/40"
              >
                Read the Full Backstory <ArrowRight className="w-3 h-3" strokeWidth={3} />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div className="panel-elev p-7">
                <div className="grid grid-cols-3 gap-px bg-[var(--border-soft)] mb-6">
                  {[
                    { k: "~20", v: "Years Experience" },
                    { k: "Both", v: "Gov + Commercial" },
                    { k: "FAR/DFARS", v: "Fluent" },
                  ].map((s) => (
                    <div key={s.v} className="bg-[var(--bg-secondary)] p-4">
                      <div className="metric-number text-2xl text-sky-400">{s.k}</div>
                      <div className="tick-label mt-1">{s.v}</div>
                    </div>
                  ))}
                </div>
                <blockquote className="border-l-2 border-sky-500/60 pl-5">
                  <p className="font-display text-slate-200 text-lg leading-relaxed italic mb-3">
                    &ldquo;Most contractors lose more money on the wrong RFPs than on the proposals
                    they lose. The decision to <em>bid</em> is the most expensive decision in capture
                    — and it&apos;s usually made on instinct. GovBidIQ replaces instinct with
                    evidence.&rdquo;
                  </p>
                  <div className="tick-label text-slate-500">— GovBidIQ Founder</div>
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FREE ASSESSMENT LEAD MAGNET */}
      <section id="free-assessment" className="border-b border-[var(--border-soft)] bg-[var(--bg-secondary)]/40 scroll-mt-20">
        <div className="max-w-[1200px] mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="tick-label text-sky-400 mb-3">/ Try Before You Buy</div>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-slate-100 leading-tight mb-5">
                Get a free preview on one of your real opportunities.
              </h2>
              <p className="text-slate-300 text-base leading-relaxed">
                Tell us about an active solicitation you&apos;re weighing. We&apos;ll run a
                complimentary preview Opportunity Intelligence Report and email it back to you —
                no credit card, no commitment.
              </p>
            </div>
            <div className="lg:col-span-7">
              <LeadCapture variant="inline" source="landing_free_assessment" />
            </div>
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="border-b border-[var(--border-soft)] bg-[var(--bg-secondary)]/40">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <div className="tick-label text-sky-400 mb-3">/ Pricing</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-slate-100 mb-3">
              Pay per analysis. No subscriptions.
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Three tiers calibrated to how senior teams actually run capture pursuits.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-[var(--border-soft)] max-w-5xl mx-auto">
            {PRICE_TEASERS.map((p) => (
              <div key={p.name} data-testid={`landing-tier-${p.id}`} className="bg-[var(--bg-primary)] p-7 text-center">
                <h3 className="font-display font-bold text-slate-100 mb-1">{p.name}</h3>
                <div className="tick-label mb-4">{p.tagline}</div>
                <div className="mb-5">
                  <span className="metric-number text-4xl text-sky-400">${p.price}</span>
                  <span className="font-mono text-xs text-slate-500 ml-1.5">/ report</span>
                </div>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 border border-slate-700 hover:border-sky-500 hover:text-sky-400 text-slate-200 font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5"
                >
                  Learn More
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 text-center">
          <Award className="w-10 h-10 text-sky-400 mx-auto mb-6" strokeWidth={1.4} />
          <h2 className="font-display font-black text-3xl sm:text-4xl text-slate-100 max-w-3xl mx-auto mb-5 leading-tight">
            Stop chasing the wrong RFPs.
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-8">
            Most contractors waste 60-70% of proposal labor on opportunities they cannot win.
            GovBidIQ separates signal from noise — fast.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="#free-assessment"
              data-testid="landing-bottom-cta-free"
              onClick={() => trackCTA("landing-bottom-free-assessment")}
              className="inline-flex items-center gap-3 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-xs tracking-[0.2em] uppercase font-bold px-7 py-4 transition-colors"
            >
              <Sparkles className="w-4 h-4" strokeWidth={2.5} /> Free Assessment
            </a>
            <Link
              to="/pricing"
              data-testid="landing-bottom-cta-pricing"
              onClick={() => trackCTA("landing-bottom-pricing")}
              className="inline-flex items-center gap-3 border border-sky-500/60 hover:bg-sky-500/10 text-sky-300 font-mono text-xs tracking-[0.2em] uppercase font-bold px-7 py-4 transition-colors"
            >
              View Pricing <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--border-soft)]">
        <div className="max-w-[1400px] mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-4">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">
            © GovBidIQ · Contract Intelligence
          </div>
          <div className="flex items-center gap-5 font-mono text-[10px] tracking-[0.2em] uppercase">
            <Link to="/who-its-for" className="text-slate-400 hover:text-sky-400">Who It&apos;s For</Link>
            <Link to="/about" className="text-slate-400 hover:text-sky-400">About</Link>
            <Link to="/pricing" className="text-slate-400 hover:text-sky-400">Pricing</Link>
            <Link to="/disclaimer" className="text-slate-400 hover:text-sky-400">Disclaimer</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
